<template>
  <section v-if="midAutumnBannerVisible" class="mid-autumn-banner mb-4" aria-label="2026 中秋群發活動">
    <div class="mid-autumn-banner__controls">
      <button type="button" class="mid-autumn-banner__close" aria-label="關閉中秋活動橫幅 24 小時"
              @click="dismissMidAutumnPromotion">×</button>
    </div>
    <div ref="art" class="mid-autumn-banner__art">
      <img :src="bannerImage" :width="bannerLayout.width" :height="bannerLayout.height"
           alt="2026 中秋新套版：中秋衛教卡已備好，現在就能 LINE 群發。維生素 ABCD、五大 NG 飲食習慣，把中秋關心分享給親友。"/>
      <template v-for="action in actions">
        <router-link v-if="action.to" :key="action.label" :to="action.to" :aria-label="action.label"
                     class="mid-autumn-banner__hit" :style="action.area"/>
        <a v-else :key="action.label" :href="action.href" target="_blank" rel="noopener noreferrer"
           :aria-label="action.label" class="mid-autumn-banner__hit" :style="action.area"/>
      </template>
    </div>
  </section>
</template>

<script>
import midAutumnPromotion from '@/mixins/midAutumnPromotion'
import { MID_AUTUMN_PREVIEW_URL, MID_AUTUMN_SUPPORT_URL } from '@/utils/midAutumnPromotion'
import homeDesktop from '@/assets/mid-autumn-2026/crm-home-desktop-v7.png'
import homeTablet from '@/assets/mid-autumn-2026/crm-home-tablet-v7.png'
import homeMobile from '@/assets/mid-autumn-2026/crm-home-mobile-v7.png'
import sendDesktop from '@/assets/mid-autumn-2026/crm-send-desktop-v5.png'
import sendTablet from '@/assets/mid-autumn-2026/crm-send-tablet-v5.png'
import sendMobile from '@/assets/mid-autumn-2026/crm-send-mobile-v5.png'

const images = {
  home: {desktop: homeDesktop, tablet: homeTablet, mobile: homeMobile},
  send: {desktop: sendDesktop, tablet: sendTablet, mobile: sendMobile}
}
// Pixel bounds measured from the official home v7 / send v5 PNGs.
// Each action is [top, height]; support text includes surrounding touch space.
const layouts = {
  home: {
    desktop: {width: 4800, height: 720, left: 3960, buttonWidth: 760, areas: [[156, 128], [308, 128], [472, 96]]},
    tablet: {width: 2880, height: 432, left: 2256, buttonWidth: 576, areas: [[60, 112], [192, 112], [328, 88]]},
    mobile: {width: 1440, height: 720, left: 776, buttonWidth: 616, areas: [[260, 128], [412, 128], [564, 100]]}
  },
  send: {
    desktop: {width: 4800, height: 720, left: 3824, buttonWidth: 896, areas: [[166, 176], [378, 176]]},
    tablet: {width: 2880, height: 720, left: 1996, buttonWidth: 820, areas: [[166, 176], [378, 176]]},
    mobile: {width: 1440, height: 976, left: 720, buttonWidth: 672, areas: [[432, 176], [640, 176]]}
  }
}

export default {
  name: 'MidAutumnBanner',
  mixins: [midAutumnPromotion],
  props: { variant: { type: String, default: 'home' } },
  data() { return {bannerWidth: 0} },
  computed: {
    bannerSize() {
      const [desktop, tablet] = this.variant === 'home' ? [1000, 600] : [1200, 720]
      return this.bannerWidth >= desktop ? 'desktop' : this.bannerWidth >= tablet ? 'tablet' : 'mobile'
    },
    bannerLayout() { return layouts[this.variant === 'home' ? 'home' : 'send'][this.bannerSize] },
    bannerImage() { return images[this.variant === 'home' ? 'home' : 'send'][this.bannerSize] },
    actions() {
      const home = this.variant === 'home'
      const layout = this.bannerLayout
      const areas = layout.areas
      return [
        ...(home ? [{ label: '開始中秋群發', to: '/message/send/line' }] : []),
        { label: '預覽中秋卡片', href: MID_AUTUMN_PREVIEW_URL },
        { label: '聯繫客服協助發送', href: MID_AUTUMN_SUPPORT_URL }
      ].map((action, index) => ({...action, area: {
        left: `${layout.left / layout.width * 100}%`,
        width: `${layout.buttonWidth / layout.width * 100}%`,
        top: `${areas[index][0] / layout.height * 100}%`,
        height: `${areas[index][1] / layout.height * 100}%`
      }}))
    }
  },
  watch: {
    midAutumnBannerVisible() { this.$nextTick(this.observeBanner) }
  },
  mounted() {
    this.observeBanner()
    window.addEventListener('resize', this.measureBanner)
  },
  beforeDestroy() {
    if (this.bannerObserver) this.bannerObserver.disconnect()
    if (typeof window !== 'undefined') window.removeEventListener('resize', this.measureBanner)
  },
  methods: {
    measureBanner() {
      const art = this.$refs.art
      if (art && art.getBoundingClientRect().width > 0) this.bannerWidth = art.getBoundingClientRect().width
    },
    observeBanner() {
      if (this.bannerObserver) this.bannerObserver.disconnect()
      if (this._isDestroyed || !this.$refs.art) return
      this.measureBanner()
      if (typeof window.ResizeObserver === 'function') {
        this.bannerObserver = new window.ResizeObserver(entries => {
          for (const entry of entries) {
            if (entry.target === this.$refs.art && entry.contentRect.width > 0) this.bannerWidth = entry.contentRect.width
          }
        })
        this.bannerObserver.observe(this.$refs.art)
      }
    }
  }
}
</script>

<style scoped>
.mid-autumn-banner { width: 100%; min-width: 0; }
.mid-autumn-banner__controls { display: flex; justify-content: flex-end; padding-bottom: 4px; }
.mid-autumn-banner__art { position: relative; background: #fff9e6; }
.mid-autumn-banner__art img { display: block; width: 100%; height: auto; }
.mid-autumn-banner__hit { position: absolute; border-radius: 5px; }
.mid-autumn-banner__hit:focus { outline: 3px solid #005f68; outline-offset: 2px; }
.mid-autumn-banner__close { width: 32px; height: 32px; border-radius: 50%; background: #fff; color: #333; font-size: 24px; line-height: 1; }
.mid-autumn-banner__close:focus { outline: 2px solid #005f68; }
</style>
